import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ComplianceDueDate, formatComplianceDate } from "../../hse/compliance/ComplianceBits";
import {
  chapterDepth,
  chapterLabel,
  facetOptions,
  filterLibrary,
  groupByChapter,
  LIBRARY_STATUS_FILTERS,
  mustReadList,
  NO_CHAPTER,
  readState,
} from "./handbookLibrary";

const MUST_READ_PREVIEW = 5;

/**
 * Read status for one handbook document (§8 status badges + deadline). Overdue shows the badge and
 * the red date only (no second FORFALT flag); pending keeps the SNART flag from ComplianceDueDate.
 */
export function HandbookReadStatus({ doc, inline = false }) {
  const { t, i18n } = useTranslation();
  const state = readState(doc);
  const dueAt = doc?.current_user_read_status?.requirement?.due_at;
  const badge = {
    form: ["pm-badge--accent", t("documentForms.hse.fillable_badge")],
    exempt: ["", t("employee_handbook.read_status_creator_exempt")],
    done: ["pm-badge--ok", t("employee_handbook.read_status_acknowledged")],
    optional: ["", t("hseCompliance.status.optional")],
    overdue: ["pm-badge--bad", t("hseCompliance.status.overdue")],
    pending: ["pm-badge--warn", t("employee_handbook.read_status_pending")],
  }[state];
  let due = null;
  if (state === "overdue" && dueAt) {
    due = <span className="pm-due pm-due--overdue"><span>{formatComplianceDate(dueAt, i18n.language)}</span></span>;
  } else if (state === "pending" && dueAt) {
    due = <ComplianceDueDate dueAt={dueAt} overdue={false} />;
  }
  return (
    <span className={inline ? "flex flex-wrap items-center gap-2" : "flex shrink-0 flex-col items-end gap-1"}>
      <span className={`pm-badge ${badge[0]}`.trim()}>{badge[1]}</span>
      {due ? <span className="text-[12px]">{due}</span> : null}
    </span>
  );
}

function DocumentRow({ doc, onOpen, showChapter = false }) {
  const meta = [doc.document_type_display, showChapter && doc.chapter ? chapterLabel(doc.chapter) : null].filter(Boolean);
  return (
    <button type="button" className="pm-doc-row" onClick={() => onOpen(doc)}>
      <span className="pm-doc-row__code hidden sm:block">{doc.document_number || "—"}</span>
      <span className="min-w-0 flex-1">
        <span className="pm-doc-row__title block">{doc.title}</span>
        <span className="pm-doc-row__meta block">
          {doc.document_number ? <span className="mr-1.5 font-mono text-[11.5px] sm:hidden">{doc.document_number}</span> : null}
          {meta.join(" · ")}
        </span>
        {/* On phones the status sits under the title so long titles keep the full width. */}
        <span className="mt-2 block sm:hidden">
          <HandbookReadStatus doc={doc} inline />
        </span>
      </span>
      <span className="hidden sm:block">
        <HandbookReadStatus doc={doc} />
      </span>
      <ChevronRightIcon className="h-4 w-4 shrink-0 text-[color:var(--pls-faint)]" aria-hidden="true" />
    </button>
  );
}

/**
 * Personal handbook → Dokumenter (PROMANG_COMPONENTS.md §8.5): «Må leses» first, then search,
 * status/type/fag filters and one flat list grouped per chapter, with a chapter index on desktop.
 * Replaces the old fag → kategori → kapittel → underkapittel tree.
 */
export function HandbookDocumentLibrary({ documents = [], onOpen, initialStatus = "all" }) {
  const { t } = useTranslation();
  const searchRef = useRef(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(LIBRARY_STATUS_FILTERS.includes(initialStatus) ? initialStatus : "all");
  const [type, setType] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [chapter, setChapter] = useState("");

  useEffect(() => {
    if (LIBRARY_STATUS_FILTERS.includes(initialStatus)) setStatus(initialStatus);
  }, [initialStatus]);

  // «/» focuses the search (design principle 8).
  useEffect(() => {
    const onKey = (event) => {
      const target = event.target;
      const typing = target?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName);
      if (event.key === "/" && !typing && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const mustRead = useMemo(() => mustReadList(documents), [documents]);
  const doneCount = useMemo(() => documents.filter((doc) => readState(doc) === "done").length, [documents]);
  const typeOptions = useMemo(
    () => facetOptions(documents, (doc) => doc.document_type_display && { value: doc.document_type_display, label: doc.document_type_display }),
    [documents]
  );
  const disciplineOptions = useMemo(
    () => facetOptions(documents, (doc) => doc.discipline?.id != null && { value: String(doc.discipline.id), label: doc.discipline.name }),
    [documents]
  );
  // The chapter index counts what the other filters leave, so it never points at an empty chapter.
  const withoutChapter = useMemo(
    () => filterLibrary(documents, { query, status, type, discipline }),
    [documents, query, status, type, discipline]
  );
  const chapterGroups = useMemo(() => groupByChapter(withoutChapter), [withoutChapter]);
  const groups = useMemo(
    () => (chapter ? chapterGroups.filter((group) => group.key === chapter) : chapterGroups),
    [chapterGroups, chapter]
  );
  const resultCount = groups.reduce((sum, group) => sum + group.documents.length, 0);
  const filtersActive = Boolean(query || status !== "all" || type || discipline || chapter);
  const showMustRead = mustRead.length > 0 && status === "all" && !filtersActive;

  const clearFilters = () => {
    setQuery("");
    setStatus("all");
    setType("");
    setDiscipline("");
    setChapter("");
  };
  const groupTitle = (group) => (group.key === NO_CHAPTER ? t("employee_handbook.library.no_chapter") : chapterLabel(group.chapter));
  const statusCount = { all: documents.length, required: mustRead.length, done: doneCount };

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      {showMustRead ? (
        <section className="pm-doc-list" aria-labelledby="handbook-must-read">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--pls-border)] px-4 py-3">
            <div>
              <h2 id="handbook-must-read" className="text-[15px] font-semibold text-[color:var(--pls-fg)]">
                {t("employee_handbook.library.must_read_title", { count: mustRead.length })}
              </h2>
              <p className="text-[12.5px] text-[color:var(--pls-muted)]">{t("employee_handbook.library.must_read_hint")}</p>
            </div>
            {mustRead.length > MUST_READ_PREVIEW ? (
              <button type="button" className="pm-btn pm-btn--ghost pm-btn--sm" onClick={() => setStatus("required")}>
                {t("employee_handbook.library.show_all", { count: mustRead.length })}
              </button>
            ) : null}
          </div>
          {mustRead.slice(0, MUST_READ_PREVIEW).map((doc) => (
            <DocumentRow key={doc.id} doc={doc} onOpen={onOpen} showChapter />
          ))}
        </section>
      ) : null}

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <label className="pm-picker-search min-w-0 flex-1 bg-[color:var(--pls-surface)]" style={{ height: 42, borderColor: "var(--pls-border)" }}>
            <MagnifyingGlassIcon className="h-4 w-4 text-[color:var(--pls-muted)]" aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("employee_handbook.library.search")}
              aria-label={t("employee_handbook.library.search")}
            />
            {query ? (
              <button type="button" className="pm-picker-clear" aria-label={t("employee_handbook.library.clear_search")} onClick={() => setQuery("")}>
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : null}
          </label>
          <div className="pm-segmented pm-segmented--sm md:w-[380px] md:flex-none" role="radiogroup" aria-label={t("employee_handbook.library.filter_label")}>
            {LIBRARY_STATUS_FILTERS.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={status === value}
                className={`pm-segmented__option${status === value ? " is-active" : ""}`}
                onClick={() => setStatus(value)}
              >
                {t(`employee_handbook.library.filter_${value}`)}
                <span className="ml-1 font-mono text-[11px] opacity-70">{statusCount[value]}</span>
              </button>
            ))}
          </div>
        </div>
        {typeOptions.length > 1 || disciplineOptions.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {disciplineOptions.length > 1 ? (
              <select
                className="pm-input h-[34px] w-auto py-0 text-[13px]"
                value={discipline}
                onChange={(event) => setDiscipline(event.target.value)}
                aria-label={t("employee_handbook.library.discipline_label")}
              >
                <option value="">{t("employee_handbook.library.discipline_all")}</option>
                {disciplineOptions.map((option) => (
                  <option key={option.value} value={option.value}>{`${option.label} (${option.count})`}</option>
                ))}
              </select>
            ) : null}
            {typeOptions.length > 1
              ? typeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="pm-chip"
                    aria-pressed={type === option.value}
                    onClick={() => setType(type === option.value ? "" : option.value)}
                  >
                    {option.label}
                    <span className="pm-chip__count">{option.count}</span>
                  </button>
                ))
              : null}
          </div>
        ) : null}
      </div>

      <div className="grid min-w-0 gap-5 lg:grid-cols-[240px_minmax(0,1fr)]">
        <nav className="hidden lg:block" aria-label={t("employee_handbook.library.toc")}>
          <div className="pm-toc sticky top-24">
            <p className="px-2.5 pb-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[color:var(--pls-faint)]">
              {t("employee_handbook.library.toc")}
            </p>
            <button type="button" className="pm-toc__item" aria-current={chapter === "" ? "true" : undefined} onClick={() => setChapter("")}>
              <span>{t("employee_handbook.library.toc_all")}</span>
              <span className="pm-toc__count">{withoutChapter.length}</span>
            </button>
            {chapterGroups.map((group) => (
              <button
                key={group.key}
                type="button"
                className="pm-toc__item"
                style={{ paddingLeft: 10 + Math.min(chapterDepth(group.chapter), 3) * 12 }}
                aria-current={chapter === group.key ? "true" : undefined}
                onClick={() => setChapter(chapter === group.key ? "" : group.key)}
              >
                <span className="min-w-0 truncate">{groupTitle(group)}</span>
                <span className="pm-toc__count">{group.documents.length}</span>
              </button>
            ))}
          </div>
        </nav>

        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[13px] text-[color:var(--pls-muted)]" aria-live="polite">
              {t("employee_handbook.library.results", { count: resultCount })}
            </p>
            {chapterGroups.length > 1 ? (
              <select
                className="pm-input h-[34px] w-auto max-w-[60%] py-0 text-[13px] lg:hidden"
                value={chapter}
                onChange={(event) => setChapter(event.target.value)}
                aria-label={t("employee_handbook.library.toc")}
              >
                <option value="">{t("employee_handbook.library.toc_all")}</option>
                {chapterGroups.map((group) => (
                  <option key={group.key} value={group.key}>{`${groupTitle(group)} (${group.documents.length})`}</option>
                ))}
              </select>
            ) : null}
          </div>

          {resultCount === 0 ? (
            <div className="pm-surface-card text-center">
              <div className="text-[15px] font-semibold text-[color:var(--pls-fg)]">{t("employee_handbook.library.empty_title")}</div>
              <p className="mt-1 text-[13px] text-[color:var(--pls-muted)]">{t("employee_handbook.library.empty_desc")}</p>
              {filtersActive ? (
                <button type="button" className="pm-btn pm-btn--secondary pm-btn--sm mt-3" onClick={clearFilters}>
                  {t("employee_handbook.library.clear")}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="pm-doc-list">
              {groups.map((group) => (
                <section key={group.key} aria-label={groupTitle(group)}>
                  <h3 className="pm-doc-list__group">
                    <span className="min-w-0 truncate">{groupTitle(group)}</span>
                    <span className="flex-none">{group.documents.length}</span>
                  </h3>
                  {group.documents.map((doc) => (
                    <DocumentRow key={doc.id} doc={doc} onOpen={onOpen} />
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HandbookDocumentLibrary;
